import React, { useState, useEffect } from 'react';
import { Modal as ModalRoot, DialogProps, Textarea, Body, Button } from '@indigo-ag/component-module';
import i18next from 'core/i18n';

import { text, utils } from 'shared';
import styled from 'styled-components';

const Modal = styled(ModalRoot)`
  min-width: 35rem;
`;

const ModalContent = styled.div`
  padding: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 1rem 2rem;
`;

export interface DecisionModalProps extends DialogProps {
  title: string;
  message?: string;
  closeModal: () => void;
  onConfirmAction: (notes: string) => Promise<any>;
  notesRequired?: boolean;
  canSave?: boolean;
  extraContent?: React.ReactNode;
}

export const DecisionModal: React.FunctionComponent<DecisionModalProps> = ({
  title,
  message,
  closeModal,
  open,
  onConfirmAction,
  notesRequired = false,
  canSave,
  children,
  extraContent
}) => {
  /**
   * State
   */
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setNotes('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onNotesChange = (e: React.ChangeEvent<any>) => {
    setNotes(e.target.value);

    if (notesRequired && !e.target.value) {
      setError(i18next.t('provideNote'));
    } else if (notesRequired && e.target.value) {
      setError('');
    }
  };

  const handleSave = () => {
    return onConfirmAction(notes);
  };

  const notesLabel = `${i18next.t('notes')} (${notesRequired ? i18next.t('required') : i18next.t('optional')})`;

  const canSaveDefault = (notesRequired && !!notes) || !notesRequired ? true : false;
  const canSaveFinal = typeof canSave === 'boolean' ? canSave : canSaveDefault;

  const modalContent = () => (
    <ModalContent>
      {message ? (
        <div className="flex align-content-center">
          <Body>{utils.text.firstLetterUppercase(message)}</Body>
        </div>
      ) : null}
      <div style={{ marginTop: 30 }}>
        {children}
        <Textarea label={notesLabel} onChange={onNotesChange} value={notes} error={error} />
      </div>
      {extraContent}
    </ModalContent>
  );

  const modalActions = () => (
    <ModalActions>
      <Button onClick={closeModal} variant="secondary">
        {i18next.t('cancel')}
      </Button>
      <Button onClick={handleSave} variant="primary" disabled={!canSaveFinal}>
        {i18next.t('save')}
      </Button>
    </ModalActions>
  );

  return (
    <Modal
      open={open}
      onRequestClose={closeModal}
      title={text.firstLetterUppercase(title)}
      renderContent={modalContent}
      renderActionArea={modalActions}
    />
  );
};
